import React, { useEffect, useState, useMemo } from 'react';
import { Container, Grid, IconButton, Stack, TextField } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import axiosinstance from '../../../../api/axiosinstance';
import securedInstance from '../../../../api/securedInstance';
import { toast } from 'react-toastify';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const HiringPartnerList = () => {
  const navigate = useNavigate();
  const [data, setData]         = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const fetchData = () => {
    axiosinstance.get('/api/hiring-partners')
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load hiring partners'));
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this hiring partner?')) return;
    try {
      await securedInstance.delete(`/api/hiring-partners/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const columns = useMemo(() => [
    { field: 'partner_id', headerName: 'ID', width: 60 },
    {
      field: 'logo_url', headerName: 'Logo', width: 120, sortable: false,
      renderCell: (p) => p.value ? (
        <img src={p.value} alt="" style={{ maxHeight: 32, maxWidth: 80, objectFit: 'contain' }} />
      ) : '—',
    },
    { field: 'name', headerName: 'Company Name', width: 200 },
    { field: 'is_active', headerName: 'Active', width: 90, type: 'boolean' },
    {
      field: 'action', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => navigate(`/auth/updateHiringPartner/${p.row.partner_id}`)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(p.row.partner_id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ], [navigate]);

  const rows = useMemo(() =>
    data.map((p, i) => ({ ...p, id: i + 1 })),
  [data]);

  const requestSearch = (val) => {
    setSearchValue(val);
    const q = val.toLowerCase();
    setDataRows(rows.filter((r) =>
      Object.values(r).some((v) => v != null && v.toString().toLowerCase().includes(q))
    ));
  };

  useEffect(() => { setDataRows(rows); }, [rows]);

  return (
    <Container sx={{ width: '100vw', height: '100vh', overflow: 'auto' }}>
      <Grid container>
        <Grid item xs={6}>
          <TextField
            label="Search" variant="outlined" placeholder="Search…"
            sx={{ my: 2 }} value={searchValue}
            onChange={(e) => requestSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment: (
                <IconButton size="small"
                  style={{ visibility: searchValue ? 'visible' : 'hidden' }}
                  onClick={() => requestSearch('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            autoHeight rows={dataRows} columns={columns}
            getRowHeight={() => 'auto'}
            slots={{ toolbar: CustomToolbar }}
            sx={{
              '& .MuiDataGrid-columnHeaders': { fontSize: 15 },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '12px' },
            }}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
            pageSizeOptions={[10, 20, 50]}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default HiringPartnerList;
